import {
    HistoryBookingsData,
    HotelsBookedData,
    PastPeriodKey,
    UpcomingBooking,
    UpcomingPeriodKey,
} from '../../contexts/DashboardContext';
import { BookingStatusKey } from '../../types/booking/bookingListTypes';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import 'dayjs/locale/en';
import 'dayjs/locale/fr';
import 'dayjs/locale/de';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import isBetween from 'dayjs/plugin/isBetween';
import { ISupplierBooking } from '../../types/entities/ISupplierBooking';

dayjs.extend(isBetween);
dayjs.extend(weekOfYear);

function getOrdinal(day: number, locale: string): string {
    if (locale === 'en') {
        if (day === 1 || day === 21 || day === 31) return `${day}st`;
        if (day === 2 || day === 22) return `${day}nd`;
        if (day === 3 || day === 23) return `${day}rd`;
        return `${day}th`;
    }
    return `${day}`;
}

export const cardChildNonZinantisGrid = { xs: 12, sm: 12, md: 6, lg: 4, xl: 4 };
export const cardChildZinantisGrid = { xs: 12, sm: 12, md: 6, lg: 3, xl: 3 };
export const chartCardChildGrid = { xs: 12, sm: 12, md: 12, lg: 6, xl: 6 };

export const getPeriodPastByKey = (
    periodKey: PastPeriodKey,
    date = new Date()
) => {
    const today = dayjs(date);
    const referenceDate = today.subtract(1, 'month'); // excluimos mes actual
    let start: string;
    let end: string;

    switch (periodKey) {
        case PastPeriodKey.CURRENT:
            start = today.startOf('month').format('YYYY-MM-DD');
            end = today.endOf('month').format('YYYY-MM-DD');
            break;

        case PastPeriodKey.LAST_MONTH:
            start = referenceDate.startOf('month').format('YYYY-MM-DD');
            end = referenceDate.endOf('month').format('YYYY-MM-DD');
            break;

        case PastPeriodKey.LAST_3_MONTHS:
            start = referenceDate
                .subtract(2, 'month')
                .startOf('month')
                .format('YYYY-MM-DD');
            end = referenceDate.endOf('month').format('YYYY-MM-DD');
            break;

        case PastPeriodKey.LAST_6_MONTHS:
            start = referenceDate
                .subtract(5, 'month')
                .startOf('month')
                .format('YYYY-MM-DD');
            end = referenceDate.endOf('month').format('YYYY-MM-DD');
            break;

        case PastPeriodKey.LAST_12_MONTHS:
            start = referenceDate
                .subtract(11, 'month')
                .startOf('month')
                .format('YYYY-MM-DD');
            end = referenceDate.endOf('month').format('YYYY-MM-DD');
            break;

        default:
            start = today.startOf('month').format('YYYY-MM-DD');
            end = today.endOf('month').format('YYYY-MM-DD');
            break;
    }

    return { start, end };
};

export const getPeriodUpcomingByKey = (
    periodKey: UpcomingPeriodKey,
    date = new Date()
) => {
    const today = dayjs(date);
    let start: string;
    let end: string;

    switch (periodKey) {
        case UpcomingPeriodKey.NEXT_7_DAYS:
            start = today.startOf('day').format('YYYY-MM-DD');
            end = today.add(6, 'day').endOf('day').format('YYYY-MM-DD');
            break;

        case UpcomingPeriodKey.NEXT_14_DAYS:
            start = today.startOf('day').format('YYYY-MM-DD');
            end = today.add(13, 'day').endOf('day').format('YYYY-MM-DD');
            break;

        case UpcomingPeriodKey.NEXT_30_DAYS:
            start = today.startOf('day').format('YYYY-MM-DD');
            end = today.add(29, 'day').endOf('day').format('YYYY-MM-DD');
            break;

        default:
            start = today.startOf('day').format('YYYY-MM-DD');
            end = today.add(6, 'day').endOf('day').format('YYYY-MM-DD');
            break;
    }

    return { start, end };
};

export const getPeriodByKey = (
    periodKey: PastPeriodKey | UpcomingPeriodKey,
    date = new Date()
) => {
    if (
        Object.values(UpcomingPeriodKey).includes(
            periodKey as UpcomingPeriodKey
        )
    ) {
        return getPeriodUpcomingByKey(periodKey as UpcomingPeriodKey, date);
    }
    return getPeriodPastByKey(periodKey as PastPeriodKey, date);
};

export function formatPastPeriodSubtitle(
    periodKey: PastPeriodKey,
    locale: 'es' | 'en' | 'fr' | 'de',
    translate: (key: string) => string
): string {
    const today = dayjs();
    dayjs.locale(locale);

    const referenceDate = today.subtract(1, 'month'); // excluimos mes actual

    const formatMonthYear = (date: dayjs.Dayjs) => date.format('MMMM YYYY');

    const formatRange = (start: dayjs.Dayjs, end: dayjs.Dayjs) => {
        if (locale === 'en') {
            return `From ${start.format('MMMM')} ${getOrdinal(
                start.date(),
                locale
            )} to ${end.format('MMMM')} ${getOrdinal(
                end.date(),
                locale
            )}, ${end.format('YYYY')}`;
        }
        if (locale === 'fr') {
            return `Du ${start.format('D')} au ${end.format('D')} ${end.format(
                'MMMM YYYY'
            )}`;
        }
        if (locale === 'de') {
            return `Vom ${start.format('D')}. bis ${end.format(
                'D'
            )}. ${end.format('MMMM YYYY')}`;
        }
        // Español por defecto
        return `Desde el ${start.format('D')} al ${end.format(
            'D'
        )} de ${end.format('MMMM YYYY')}`;
    };

    switch (periodKey) {
        case PastPeriodKey.CURRENT: {
            const start = today.startOf('month');
            const end = today;
            return formatRange(start, end);
        }

        case PastPeriodKey.LAST_MONTH: {
            const start = referenceDate.startOf('month');
            const end = referenceDate.endOf('month');
            return formatRange(start, end);
        }

        case PastPeriodKey.LAST_3_MONTHS: {
            const start = referenceDate.subtract(2, 'month').startOf('month');
            const end = referenceDate.endOf('month');
            return `${translate('from')} ${formatMonthYear(start)} ${translate(
                'to'
            )} ${formatMonthYear(end)}`;
        }

        case PastPeriodKey.LAST_6_MONTHS: {
            const start = referenceDate.subtract(5, 'month').startOf('month');
            const end = referenceDate.endOf('month');
            return `${translate('from')} ${formatMonthYear(start)} ${translate(
                'to'
            )} ${formatMonthYear(end)}`;
        }

        case PastPeriodKey.LAST_12_MONTHS: {
            const start = referenceDate.subtract(11, 'month').startOf('month');
            const end = referenceDate.endOf('month');
            return `${translate('from')} ${formatMonthYear(start)} ${translate(
                'to'
            )} ${formatMonthYear(end)}`;
        }

        default:
            return '';
    }
}

export function formatUpcomingPeriodSubtitle(
    periodKey: UpcomingPeriodKey,
    locale: 'es' | 'en' | 'fr' | 'de'
): string {
    const today = dayjs();
    dayjs.locale(locale);

    const daysMap: Record<UpcomingPeriodKey, number> = {
        [UpcomingPeriodKey.NEXT_7_DAYS]: 6,
        [UpcomingPeriodKey.NEXT_14_DAYS]: 13,
        [UpcomingPeriodKey.NEXT_30_DAYS]: 29,
    };

    const days = daysMap[periodKey];
    const endDate = today.add(days, 'day');

    const formatDate = (date: dayjs.Dayjs) => {
        if (locale === 'en') {
            return `${date.format('MMMM')} ${getOrdinal(date.date(), locale)}`;
        }
        if (locale === 'fr') {
            return `${date.format('D')} ${date.format('MMMM')}`;
        }
        if (locale === 'de') {
            return `${date.format('D')}. ${date.format('MMMM')}`;
        }
        return `${date.format('D')} de ${date.format('MMMM')}`;
    };

    const labelMap: Record<string, string> = {
        es: `Hoy y los próximos ${days} días (hasta el ${formatDate(endDate)})`,
        en: `Today and the next ${days} days (until ${formatDate(endDate)})`,
        fr: `Aujourd'hui et les ${days} prochains jours (jusqu'au ${formatDate(
            endDate
        )})`,
        de: `Heute und die nächsten ${days} Tage (bis ${formatDate(endDate)})`,
    };

    return labelMap[locale] ?? labelMap['es'];
}

export const getChartPeriodsByKey = (
    periodKey: PastPeriodKey,
    language: string
) => {
    dayjs.locale(language);
    const today = dayjs();
    const referenceDate = today.subtract(1, 'month'); // excluimos mes actual

    const periods: {
        label: string;
        longLabel: string;
        start: string;
        end: string;
    }[] = [];

    const pushPeriod = (
        start: dayjs.Dayjs,
        end: dayjs.Dayjs,
        label: string,
        longLabel: string
    ) => {
        periods.push({
            label,
            longLabel,
            start: start.startOf('day').format(), // normalizado
            end: end.endOf('day').format(), // normalizado
        });
    };

    switch (periodKey) {
        case PastPeriodKey.LAST_12_MONTHS:
            for (let i = 11; i >= 0; i--) {
                const start = referenceDate
                    .subtract(i, 'month')
                    .startOf('month');
                const end = referenceDate.subtract(i, 'month').endOf('month');
                pushPeriod(
                    start,
                    end,
                    start.format('MMM'),
                    start.format('MMMM YYYY')
                );
            }
            break;

        case PastPeriodKey.LAST_6_MONTHS:
            for (let i = 5; i >= 0; i--) {
                const start = referenceDate
                    .subtract(i, 'month')
                    .startOf('month');
                const end = referenceDate.subtract(i, 'month').endOf('month');
                pushPeriod(
                    start,
                    end,
                    start.format('MMM'),
                    start.format('MMMM YYYY')
                );
            }
            break;

        case PastPeriodKey.LAST_3_MONTHS: {
            const start = referenceDate.subtract(2, 'month').startOf('month');
            const end = referenceDate.endOf('month');
            let current = start;

            const weekLongByLanguage: Record<string, string> = {
                es: 'Semana',
                en: 'Week',
                fr: 'Semaine',
                de: 'Woche',
            };
            const weekLong = weekLongByLanguage[language] || 'Week';
            const weekShort = weekLong.charAt(0);

            while (current.isBefore(end) || current.isSame(end, 'week')) {
                const weekStart = current.startOf('week');
                const weekEnd = current.endOf('week');
                pushPeriod(
                    weekStart,
                    weekEnd,
                    `${weekShort}${current.week()}`, // Short label: solo inicial
                    `${weekLong} ${current.week()}: ${weekStart.format(
                        'D MMM'
                    )} - ${weekEnd.format('D MMM')}` // Long label completo
                );
                current = current.add(1, 'week');
            }
            break;
        }

        case PastPeriodKey.LAST_MONTH: {
            const daysInMonth = referenceDate.daysInMonth();
            for (let i = 1; i <= daysInMonth; i++) {
                const current = referenceDate.date(i);
                pushPeriod(
                    current.startOf('day'),
                    current.endOf('day'),
                    `${i}`,
                    current.format('D MMM YYYY')
                );
            }
            break;
        }

        case PastPeriodKey.CURRENT: {
            const daysInMonth = today.date();
            for (let i = 1; i <= daysInMonth; i++) {
                const current = today.date(i);
                pushPeriod(
                    current.startOf('day'),
                    current.endOf('day'),
                    `${i}`,
                    current.format('D MMM YYYY')
                );
            }
            break;
        }

        default:
            break;
    }

    return periods;
};

export const getData = (
    rawData: HistoryBookingsData[],
    periodKey: PastPeriodKey,
    language: string
) => {
    const periods = getChartPeriodsByKey(periodKey, language);

    const periodsArray = periods.map(period => ({
        ...period,
        committedCount: 0,
        cancelledCount: 0,
    }));

    rawData.forEach(item => {
        const itemDate = dayjs(item.DateStats);

        for (const periodEntry of periodsArray) {
            const start = dayjs(periodEntry.start);
            const end = dayjs(periodEntry.end);

            if (itemDate.isBetween(start, end, null, '[]')) {
                periodEntry.committedCount += item.CommitedCount;
                periodEntry.cancelledCount += item.CancelledCount;
                break;
            }
        }
    });
    return periodsArray;
};

export const formatMostBookedHotelsData = (data: HotelsBookedData[]) => {
    const sortedData = data.sort((a, b) => b.BookingCount - a.BookingCount);
    const cutMostBookedHotelsDataArray =
        sortedData.length > 6 ? sortedData.slice(0, 6) : sortedData;
    return cutMostBookedHotelsDataArray;
};

export const formatUpcomingBookingsData = (
    data: ISupplierBooking[]
): UpcomingBooking[] => {
    return data.map(booking => ({
        BookingCode: booking.BookingCode,
        Id: booking.Id,
        IdSupplierHotel: booking.IdSupplierHotel,
        SupplierHotel: booking.SupplierHotel.Name,
        CheckInDate: booking.CheckInDate,
        CheckOutDate: booking.CheckOutDate,
        Nights: booking.Nights,
    }));
};

export const mostBookedColorsArray = [
    '#0075a4',
    '#008fad',
    '#00a697',
    '#1eb769',
    '#94c031',
    '#f3bc00',
];

export enum BookingStatusColor {
    COMMITTED = '#1f9b42',
    CANCELLED = '#b50027',
}

export const getUpcomingCheckInDiffDays = (date: Date | string) => {
    const checkIn = dayjs(date).startOf('day');
    const today = dayjs().startOf('day');
    return checkIn.diff(today, 'day');
};

export const UPCOMING_BOOKINGS_STATUS_KEY = BookingStatusKey.COMMITTED;
